import Image from 'next/image';

import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/features/experiments/ab_plp_redesign_2025/components/section-header';
import { CheckGlyph } from '~/features/government/components/check-glyph';
import {
  impactInfluenceContent,
  ImpactInfluenceGroup,
  impactInfluenceGroups,
} from '~/features/government/configs/impact-influence-content';

// Dark gradient background matches Figma node 159:24851 (desktop) / 159:25359 (mobile).
// Left → right: #001E65 (deeper than blue.700) → #0A0B0F (≈ grey.900).
const BACKGROUND_GRADIENT = 'linear-gradient(90deg, #001E65 0%, #0A0B0F 100%)';

// Blue-200 (#AFC6FF) is not in the v2 theme; reuse the established literal already used by
// neighboring `government-*` sections (see government-societal-impact.tsx).
const PRETITLE_COLOR = '#AFC6FF';

export type GovernmentImpactInfluenceProps = {
  pretitle?: string;
  heading?: string;
  intro?: string;
  imageSrc?: string;
  imageAlt?: string;
  eyebrowIconSrc?: string;
  groups?: ImpactInfluenceGroup[];
};

export function GovernmentImpactInfluence({
  pretitle = impactInfluenceContent.pretitle,
  heading = impactInfluenceContent.heading,
  intro = impactInfluenceContent.intro,
  imageSrc = impactInfluenceContent.imageSrc,
  imageAlt = impactInfluenceContent.imageAlt,
  eyebrowIconSrc = impactInfluenceContent.eyebrowIconSrc,
  groups = impactInfluenceGroups,
}: GovernmentImpactInfluenceProps) {
  return (
    <Box as='section' backgroundImage={BACKGROUND_GRADIENT} py={{ base: '48px', md: '96px' }}>
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex
          alignItems='center'
          direction='column'
          gap={{ base: '32px', md: '64px' }}
          margin='0 auto'
          maxW={{ base: '100%', md: '1056px' }}
        >
          <SectionHeader
            color={PRETITLE_COLOR}
            containerProps={{ alignItems: 'center', maxW: { base: '100%', md: '925px' }, w: '100%' }}
            subtitle={pretitle}
            descriptionSlot={
              <Text
                color='blue.100'
                fontSize={{ base: '0.875rem', md: '1rem' }}
                letterSpacing={{ base: '-0.01em', md: 'normal' }}
                lineHeight={{ base: '1.4', md: '1.5' }}
              >
                {intro}
              </Text>
            }
            iconSlot={
              <Box h={{ base: '14px', md: '18px' }} position='relative' w={{ base: '14px', md: '18px' }}>
                <Image alt='' src={eyebrowIconSrc} style={{ objectFit: 'contain' }} fill />
              </Box>
            }
            titleSlot={
              <Heading
                as='h2'
                color='white'
                fontFamily='heading'
                fontSize={{ base: '2.375rem', md: '3.375rem' }}
                fontWeight='600'
                letterSpacing='-0.02em'
                lineHeight='1.1'
              >
                {heading}
              </Heading>
            }
          />
          <ContentCard groups={groups} imageAlt={imageAlt} imageSrc={imageSrc} />
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function ContentCard({
  groups,
  imageAlt,
  imageSrc,
}: {
  groups: ImpactInfluenceGroup[];
  imageAlt: string;
  imageSrc: string;
}) {
  return (
    <Flex
      bg='rgba(123, 162, 255, 0.06)'
      border='1px solid'
      // Desktop value approximates the Figma white@0.56 OVERLAY stroke,
      // which would otherwise render too bright as a flat CSS border.
      borderColor={{ base: 'rgba(123, 162, 255, 0.08)', md: 'rgba(210, 223, 255, 0.16)' }}
      borderRadius='12px'
      direction={{ base: 'column', md: 'row' }}
      gap={{ base: '12px', md: '32px' }}
      overflow='hidden'
      p={{ base: '12px', md: '24px' }}
      w='100%'
    >
      <Box
        borderRadius='6px'
        flex={{ md: '1 0 0' }}
        h={{ base: '240px', md: 'auto' }}
        minH={{ md: '472px' }}
        overflow='hidden'
        position='relative'
        sx={{ img: { objectPosition: { base: 'top', md: 'center' } } }}
        w={{ base: '100%', md: 'auto' }}
      >
        <Image
          alt={imageAlt}
          sizes='(min-width: 48em) 488px, 100vw'
          src={imageSrc}
          style={{ objectFit: 'cover' }}
          fill
        />
      </Box>
      <Stack flex={{ md: '1 0 0' }} p={{ base: '16px', md: '32px' }} spacing={{ base: '32px', md: '48px' }} w='100%'>
        {groups.map((group) => (
          <GroupBlock key={group.id} group={group} />
        ))}
      </Stack>
    </Flex>
  );
}

function GroupBlock({ group }: { group: ImpactInfluenceGroup }) {
  return (
    <Stack spacing={{ base: '12px', md: '16px' }} w='100%'>
      <Flex
        align='center'
        borderBottom='1px solid'
        borderColor='rgba(210, 223, 255, 0.16)'
        gap='12px'
        pb={{ base: '12px', md: '16px' }}
        w='100%'
      >
        <Box flexShrink={0} h={{ base: '21px', md: '24px' }} position='relative' w={{ base: '21px', md: '24px' }}>
          <Image alt={group.iconAlt} src={group.iconSrc} style={{ objectFit: 'contain' }} fill />
        </Box>
        <Heading
          as='h3'
          color='white'
          flex='1'
          fontFamily='heading'
          fontSize={{ base: '1.125rem', md: '1.25rem' }}
          fontWeight='600'
          letterSpacing='-0.01em'
          lineHeight='1.2'
        >
          {group.heading}
        </Heading>
      </Flex>
      <Stack spacing={{ base: '12px', md: '16px' }} w='100%'>
        {group.items.map((item) => (
          <ChecklistRow key={item} text={item} />
        ))}
      </Stack>
    </Stack>
  );
}

function ChecklistRow({ text }: { text: string }) {
  return (
    <Flex align='center' gap='12px' px='3px' w='100%'>
      <Flex
        align='center'
        color='blue.100'
        flexShrink={0}
        h={{ base: '14px', md: '18px' }}
        justify='center'
        w={{ base: '14px', md: '18px' }}
      >
        <CheckGlyph />
      </Flex>
      <Text
        color='blue.100'
        fontSize={{ base: '0.875rem', md: '1rem' }}
        letterSpacing={{ base: '-0.01em', md: 'normal' }}
        lineHeight={{ base: '1.4', md: '1.5' }}
      >
        {text}
      </Text>
    </Flex>
  );
}
